import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCard, Check, Clock, X, RotateCcw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/utils";

const MAX_POLLS = 8;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const cancelled = params.get("cancelled");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [amount, setAmount] = useState(null);
  const [code, setCode] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId || cancelled) return;
    let live = true;
    const tick = async () => {
      try {
        const s = await api.paymentStatus(sessionId);
        if (!live) return;
        setAmount(s.amount);
        setCode(s.booking_code);
        if (s.payment_status === "paid") {
          setState("paid");
          queryClient.invalidateQueries({ queryKey: ["booking", s.booking_id] });
          queryClient.invalidateQueries({ queryKey: ["bookings"] });
          return;
        }
        if (s.payment_status === "failed" || s.payment_status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("pending");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      live = false;
    };
  }, [sessionId, cancelled, queryClient]);

  const meta = {
    checking: { icon: CreditCard, title: "Confirming payment…", body: "Hang tight while we confirm with Stripe.", tint: "bg-primary", spin: true },
    paid: { icon: Check, title: "Payment received", body: `${code ? `${code} · ` : ""}${amount != null ? money(amount) : ""} paid online. Your garments are all set.`, tint: "bg-primary" },
    pending: { icon: Clock, title: "Payment processing", body: "Stripe is still confirming. We'll update your order automatically.", tint: "bg-[hsl(var(--warning))]" },
    failed: { icon: X, title: "Payment didn't go through", body: "No charge was made. You can try again or pay at pickup.", tint: "bg-destructive" },
    cancelled: { icon: RotateCcw, title: "Payment cancelled", body: "No charge was made. Your booking is saved — pay at pickup or try again.", tint: "bg-muted-foreground" },
  };
  const m = meta[state];
  const Icon = m.icon;

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-16 text-center">
      <div className={cn("flex h-20 w-20 items-center justify-center rounded-full text-primary-foreground", m.tint)}>
        {m.spin ? <Loader2 className="h-9 w-9 animate-spin" /> : <Icon className="h-9 w-9" />}
      </div>
      <h1 data-testid={`payment-result-${state}`} className="mt-6 font-display text-2xl font-bold">{m.title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{m.body}</p>
      <div className="mt-8 w-full space-y-2">
        {bookingId ? <button onClick={() => navigate(`/order/${bookingId}`)} data-testid="payment-view-order-button" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">View order</button> : null}
        <button onClick={() => navigate("/")} data-testid="payment-home-button" className="w-full rounded-full py-3 font-semibold text-primary">Back to home</button>
      </div>
    </div>
  );
}
